import api from './api';

/**
 * Media upload service for handling file uploads
 */

export const uploadMediaFiles = async (files, category = 'packages') => {
  try {
    console.log('🔄 Starting file upload for', files.length, 'files');
    
    // Create FormData for multipart upload
    const formData = new FormData();
    
    // Add all files to FormData
    files.forEach((file, index) => {
      console.log(`🔍 File ${index + 1} before FormData:`, {
        name: file.name,
        size: file.size,
        type: file.type,
        constructor: file.constructor.name
      });
      formData.append('files', file);
      console.log(`📎 Added file ${index + 1}:`, file.name, `(${file.size} bytes)`);
    });
    
    // Add category
    formData.append('category', category);
    
    console.log('📤 Uploading files to /api/admin/media/upload');
    console.log('📋 FormData contents:');
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`  ${key}:`, {
          fileName: value.name || 'NO_NAME',
          size: value.size,
          type: value.type,
          constructor: value.constructor.name,
          hasNameProperty: value.hasOwnProperty('name'),
          nameDescriptor: Object.getOwnPropertyDescriptor(value, 'name')
        });
      } else {
        console.log(`  ${key}:`, value);
      }
    }
    
    const response = await api.post('/api/admin/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Add progress tracking if needed
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`📊 Upload progress: ${percentCompleted}%`);
      },
    });
    
    console.log('✅ Upload response:', response.data);
    console.log('🔍 Response structure check:', {
      hasUploadedFiles: !!response.data?.uploaded_files,
      hasSuccessfulCount: typeof response.data?.successful_count === 'number',
      responseType: typeof response.data,
      responseKeys: Object.keys(response.data || {})
    });
    
    // Handle the actual backend response format: { uploaded_files: [...], successful_count: N, ... }
    if (response.data?.uploaded_files && Array.isArray(response.data.uploaded_files)) {
      const uploadedFiles = response.data.uploaded_files;
      const successfulUploads = uploadedFiles.filter(file => file.success);
      const failedUploads = uploadedFiles.filter(file => !file.success);
      
      console.log(`✅ Successfully uploaded: ${successfulUploads.length} files`);
      if (failedUploads.length > 0) {
        console.warn(`⚠️ Failed uploads: ${failedUploads.length} files`, failedUploads);
      }
      
      return {
        success: true,
        uploadedFiles: successfulUploads,
        failedFiles: failedUploads,
        totalFiles: response.data.total_files || uploadedFiles.length,
        successfulCount: response.data.successful_count || successfulUploads.length,
        failedCount: response.data.failed_count || failedUploads.length,
        message: `Uploaded ${successfulUploads.length} of ${uploadedFiles.length} files successfully`
      };
    } else {
      console.error('❌ Invalid response format. Expected structure: { uploaded_files: [...], successful_count: N, ... }');
      console.error('❌ Actual response:', JSON.stringify(response.data, null, 2));
      throw new Error('Invalid response format from upload service');
    }
    
  } catch (error) {
    console.error('❌ File upload error:', error);
    console.error('❌ Error details:', {
      message: error.message,
      response: error.response,
      responseData: error.response?.data,
      responseStatus: error.response?.status,
      responseHeaders: error.response?.headers
    });
    
    // Extract meaningful error message
    let errorMessage = 'File upload failed';
    
    if (error.response?.data?.detail) {
      // Handle Pydantic validation errors or other detailed errors
      if (Array.isArray(error.response.data.detail)) {
        errorMessage = error.response.data.detail.map(err => {
          if (typeof err === 'object' && err.loc && err.msg) {
            return `${err.loc?.join('.')}: ${err.msg}`;
          }
          return err.toString();
        }).join(', ');
      } else if (typeof error.response.data.detail === 'string') {
        errorMessage = error.response.data.detail;
      } else {
        errorMessage = JSON.stringify(error.response.data.detail);
      }
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data) {
      // Handle cases where the error data is an object but not in expected format
      try {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } catch (e) {
        errorMessage = `Server error (${error.response.status})`;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.error('❌ Parsed error message:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const getMediaDetails = async (mediaId) => {
  try {
    console.log('🔍 Getting media details for ID:', mediaId);
    const response = await api.get(`/api/admin/media/${mediaId}`);
    console.log('✅ Media details response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Get media details error:', error);
    throw new Error(extractErrorMessage(error));
  }
};

export const deleteMediaAsset = async (mediaId) => {
  try {
    console.log('🗑️ Deleting media asset:', mediaId);
    const response = await api.delete(`/api/admin/media/${mediaId}`);
    console.log('✅ Delete media response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Delete media error:', error);
    throw new Error(extractErrorMessage(error));
  }
};

export const listMediaAssets = async (filters = {}, page = 1, limit = 20) => {
  try {
    const params = {
      page,
      limit,
      ...filters
    };
    
    console.log('📋 Listing media assets with params:', params);
    
    const response = await api.get('/api/admin/media/', { params });
    console.log('✅ List media assets response:', response.data);
    
    // Process the response to ensure consistent format
    if (response.data?.media_assets && Array.isArray(response.data.media_assets)) {
      const processedAssets = response.data.media_assets.map(buildMediaDisplayObject);
      
      return {
        ...response.data,
        media_assets: processedAssets
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ List media assets error:', error);
    throw new Error(extractErrorMessage(error));
  }
};

// Helper function to validate file types
export const validateFiles = (files, options = {}) => {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    maxFiles = 10
  } = options;
  
  const errors = [];
  const validFiles = [];
  
  if (files.length > maxFiles) {
    errors.push(`Too many files. Maximum ${maxFiles} files allowed.`);
    return { validFiles: [], errors };
  }
  
  files.forEach((file, index) => {
    const fileNumber = index + 1;
    
    // Check file size
    if (file.size > maxSize) {
      errors.push(`File ${fileNumber} (${file.name}) is too large. Maximum size: ${maxSize / (1024 * 1024)}MB`);
      return;
    }
    
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      errors.push(`File ${fileNumber} (${file.name}) has invalid type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }
    
    // If validation passes, add to valid files
    validFiles.push(file);
  });
  
  return { validFiles, errors };
};

// Helper function to create image preview URLs
export const createImagePreviews = (files) => {
  return files.map(file => ({
    file,
    preview: URL.createObjectURL(file),
    name: file.name,
    size: file.size
  }));
};

// Helper function to cleanup preview URLs (call when component unmounts)
export const cleanupPreviews = (previews) => {
  previews.forEach(preview => {
    if (preview.preview) {
      URL.revokeObjectURL(preview.preview);
    }
  });
};

// Asset types constants
export const ASSET_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document'
};

// Media categories constants
export const MEDIA_CATEGORIES = {
  PACKAGES: 'packages',
  TESTIMONIALS: 'testimonials',
  GALLERIES: 'galleries',
  GENERAL: 'general',
  HERO_IMAGES: 'hero_images',
  BLOG: 'blog'
};

// Get media categories from backend
export const getMediaCategories = async () => {
  try {
    console.log('🔄 Fetching media categories from backend');
    const response = await api.get('/api/admin/media/categories/list');
    console.log('✅ Media categories response:', response.data);
    
    if (response.data?.categories && Array.isArray(response.data.categories)) {
      return {
        success: true,
        categories: response.data.categories,
        message: 'Categories retrieved successfully'
      };
    } else {
      console.error('❌ Invalid categories response format');
      return {
        success: false,
        categories: [],
        message: 'Invalid response format'
      };
    }
  } catch (error) {
    console.error('❌ Get media categories error:', error);
    return {
      success: false,
      categories: [],
      message: extractErrorMessage(error)
    };
  }
};

// Search media assets
export const searchMediaAssets = async (searchQuery, filters = {}, page = 1, limit = 20) => {
  try {
    console.log('🔍 Searching media assets:', searchQuery, filters);
    
    const formData = new FormData();
    formData.append('query', searchQuery);
    formData.append('page', page.toString());
    formData.append('limit', limit.toString());
    
    if (filters.category) {
      formData.append('category', filters.category);
    }
    if (filters.asset_type) {
      formData.append('asset_type', filters.asset_type);
    }
    
    const response = await api.post('/api/admin/media/search', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('✅ Search response:', response.data);
    
    if (response.data?.search_results && Array.isArray(response.data.search_results)) {
      const processedResults = response.data.search_results.map(buildMediaDisplayObject);
      
      return {
        success: true,
        media_assets: processedResults,
        pagination: response.data.pagination || {},
        filters_applied: response.data.filters_applied || {},
        message: `Found ${processedResults.length} matching assets`
      };
    } else {
      console.error('❌ Invalid search response format');
      throw new Error('Invalid search response format');
    }
  } catch (error) {
    console.error('❌ Search media assets error:', error);
    throw new Error(extractErrorMessage(error));
  }
};

// Update media asset
export const updateMediaAsset = async (mediaId, updateData) => {
  try {
    console.log('✏️ Updating media asset:', mediaId, updateData);
    
    const formData = new FormData();
    
    if (updateData.alt_text !== undefined) {
      formData.append('alt_text', updateData.alt_text || '');
    }
    if (updateData.caption !== undefined) {
      formData.append('caption', updateData.caption || '');
    }
    if (updateData.category !== undefined) {
      formData.append('category', updateData.category || '');
    }
    if (updateData.tags !== undefined) {
      formData.append('tags', JSON.stringify(updateData.tags || []));
    }
    if (updateData.is_public !== undefined) {
      formData.append('is_public', updateData.is_public.toString());
    }
    
    const response = await api.put(`/api/admin/media/${mediaId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('✅ Update response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Update media asset error:', error);
    throw new Error(extractErrorMessage(error));
  }
};

// Bulk update media assets
export const bulkUpdateMediaAssets = async (mediaIds, updateData) => {
  try {
    console.log('📦 Bulk updating media assets:', mediaIds.length, 'assets', updateData);
    
    const formData = new FormData();
    
    // Add media IDs
    mediaIds.forEach(id => {
      formData.append('media_ids', id);
    });
    
    // Add update data
    if (updateData.category !== undefined && updateData.category !== '') {
      formData.append('category', updateData.category);
    }
    if (updateData.is_public !== undefined && updateData.is_public !== null) {
      formData.append('is_public', updateData.is_public.toString());
    }
    if (updateData.tags !== undefined) {
      formData.append('tags', JSON.stringify(updateData.tags || []));
    }
    
    const response = await api.post('/api/admin/media/bulk-update', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('✅ Bulk update response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Bulk update media assets error:', error);
    throw new Error(extractErrorMessage(error));
  }
};

// Build media display object - converts backend format to frontend format
export const buildMediaDisplayObject = (mediaAsset) => {
  if (!mediaAsset) return null;
  
  return {
    id: mediaAsset.id,
    title: mediaAsset.title || mediaAsset.original_filename,
    original_filename: mediaAsset.original_filename,
    file_extension: mediaAsset.file_extension,
    mime_type: mediaAsset.mime_type,
    file_size: mediaAsset.file_size_bytes || mediaAsset.file_size,
    file_size_bytes: mediaAsset.file_size_bytes || mediaAsset.file_size,
    storage_provider: mediaAsset.storage_provider,
    storage_path: mediaAsset.storage_path,
    thumbnail_path: mediaAsset.thumbnail_path,
    width: mediaAsset.width,
    height: mediaAsset.height,
    duration_seconds: mediaAsset.duration_seconds,
    asset_type: mediaAsset.asset_type,
    file_type: mediaAsset.asset_type, // alias for compatibility
    category: mediaAsset.category,
    tags: mediaAsset.tags,
    alt_text: mediaAsset.alt_text,
    caption: mediaAsset.caption,
    is_active: mediaAsset.is_active,
    is_public: mediaAsset.is_public,
    created_at: mediaAsset.created_at,
    updated_at: mediaAsset.updated_at,
    uploaded_by: mediaAsset.uploaded_by,
    // Generate URLs - these might need adjustment based on your storage setup
    url: mediaAsset.url || mediaAsset.storage_path || mediaAsset.file_url,
    file_url: mediaAsset.file_url || mediaAsset.url || mediaAsset.storage_path,
    thumbnail_url: mediaAsset.thumbnail_url || mediaAsset.thumbnail_path
  };
};

// Extract error message from API error response
export const extractErrorMessage = (error) => {
  if (!error) return 'Unknown error occurred';
  
  // If error is already a string, return it
  if (typeof error === 'string') return error;
  
  // Check for response data
  if (error.response?.data) {
    const data = error.response.data;
    
    // Handle FastAPI validation errors
    if (Array.isArray(data.detail)) {
      return data.detail.map(err => {
        if (typeof err === 'object' && err.loc && err.msg) {
          return `${err.loc?.join('.')}: ${err.msg}`;
        }
        return err.toString();
      }).join(', ');
    }
    
    // Handle string detail
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    
    // Handle message field
    if (data.message) {
      return data.message;
    }
    
    // Handle error field
    if (data.error) {
      return data.error;
    }
    
    // Try to stringify other data
    try {
      return JSON.stringify(data);
    } catch (e) {
      return `Server error (${error.response.status})`;
    }
  }
  
  // Handle network errors
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    return 'Network error - please check your connection';
  }
  
  // Handle timeout errors
  if (error.code === 'TIMEOUT' || error.message?.includes('timeout')) {
    return 'Request timeout - please try again';
  }
  
  // Return the error message if available
  if (error.message) {
    return error.message;
  }
  
  // Last resort
  return 'An unexpected error occurred';
};

// Format file size helper function
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
